import { useEffect, useState } from 'react';
import { getSetSchedule, setSchedule, type Schedule } from '../account';

type Freq = 'off' | 'weekly' | 'monthly';

/**
 * The automatic re-check of ONE question set (D-30, per set since 031).
 *
 * Each set keeps its own: a weekly check of the product-line set and a monthly
 * one of the brand set are different measurements, and one switch per domain
 * could only ever re-run whichever set happened to be active.
 *
 * Off until asked for. `initial` lets a caller that already listed the
 * schedules skip the fetch.
 */
export default function SetSchedule({ set, plan, defaultEngine = 'openai', initial, onChange }: {
  set: { id: string; domain: string };
  plan: 'free' | 'pro';
  defaultEngine?: 'openai' | 'gemini';
  initial?: Schedule | null;
  onChange?: (s: Schedule) => void;
}): JSX.Element {
  const [schedule, setLocal] = useState<Schedule | null>(initial ?? null);
  const [loaded, setLoaded] = useState(initial !== undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial !== undefined) return;
    getSetSchedule(set.id).then(setLocal).catch(() => setLocal(null)).finally(() => setLoaded(true));
  }, [set.id, initial]);

  const freq: Freq = schedule?.enabled ? schedule.frequency : 'off';
  const engine = schedule?.engine ?? defaultEngine;

  async function save(next: { freq?: Freq; engine?: 'openai' | 'gemini' }): Promise<void> {
    if (busy) return;
    const f = next.freq ?? freq;
    setBusy(true);
    setError(null);
    try {
      const s = await setSchedule({
        url: `https://${set.domain}/`,
        querySetId: Number(set.id),
        enabled: f !== 'off',
        frequency: f === 'off' ? (schedule?.frequency ?? 'weekly') : f,
        engine: next.engine ?? engine,
      });
      setLocal(s);
      onChange?.(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not change the setting');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <span className="font-mono text-xs text-text-secondary">…</span>;
  if (plan !== 'pro') {
    return <span className="font-mono text-xs text-text-secondary/80">auto re-check: Pro</span>;
  }

  const pill = (active: boolean): string =>
    `font-mono text-xs border rounded px-2 py-1 transition disabled:opacity-40 ${
      active ? 'text-cyan border-cyan/60 bg-cyan/10' : 'text-text-secondary border-warm-dark-2 hover:text-cyan hover:border-cyan/50'}`;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 flex-wrap" role="radiogroup" aria-label="automatic re-check">
        <span className="font-mono text-xs text-text-secondary mr-0.5">auto re-check</span>
        {(['off', 'weekly', 'monthly'] as const).map((f) => (
          <button key={f} type="button" role="radio" aria-checked={freq === f}
            onClick={() => void save({ freq: f })} disabled={busy} className={pill(freq === f)}>
            {f}
          </button>
        ))}
        {freq !== 'off' && (
          <>
            <span className="font-mono text-xs text-text-secondary ml-1.5">via</span>
            {(['openai', 'gemini'] as const).map((e) => (
              <button key={e} type="button" onClick={() => void save({ engine: e })} disabled={busy} className={pill(engine === e)}>
                {e === 'openai' ? 'ChatGPT' : 'Gemini'}
              </button>
            ))}
          </>
        )}
      </div>
      {schedule?.enabled && (
        <span className="font-mono text-xs text-text-secondary">
          next {new Date(schedule.nextRunAt).toLocaleDateString()}
          {schedule.lastRunAt && ` · last ran ${new Date(schedule.lastRunAt).toLocaleDateString()}`}
        </span>
      )}
      {schedule?.lastError && <span className="font-mono text-xs text-estimated">last run couldn&apos;t complete: {schedule.lastError}</span>}
      {error && <span className="font-mono text-xs text-[#E0574A]">{error}</span>}
    </div>
  );
}
